import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

class OlsunFooterSignature extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.animations = [];
    this.footer = this.closest('.oft');
    this.cover = this.footer?.querySelector('.oft__surface');
    this.source = this.querySelector('[data-footer-source]');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    const options = { signal: this.controller.signal };
    if (this.played || !this.cover || !this.source || !this.animate || !window.IntersectionObserver
      || this.motion.matches || this.dataset.disabled === 'true'
      || document.documentElement.hasAttribute('data-olsun-static-product')) { this.finish(); return; }
    // Never erase an already visible logo on a restored/reloaded bottom-of-page view.
    if (this.isUncovered()) { this.finish(); return; }
    this.classList.add('is-armed');
    this.dataset.phase = 'waiting';
    this.motion.addEventListener('change', () => this.finish(), options);
    window.addEventListener('resize', () => this.schedule(), options);
    document.addEventListener('visibilitychange', () => { if (document.hidden && this.played) this.finish(); }, options);
    this.footer.addEventListener('toggle', () => this.schedule(), { capture: true, ...options });
    scrollContainerMediaQuery.addEventListener('change', () => this.observe(), options);
    this.observe();
  }

  observe() {
    if (this.played) return;
    this.observer?.disconnect();
    this.scrollController?.abort();
    this.scrollController = new AbortController();
    const container = getScrollContainer();
    this.observer = new IntersectionObserver((entries) => {
      this.near = entries.some((entry) => entry.isIntersecting);
      this.schedule();
    }, { root: container === document.scrollingElement ? null : container, threshold: 0 });
    this.observer.observe(this.footer);
    getScrollEventTarget().addEventListener('scroll', () => this.schedule(), { passive: true, signal: this.scrollController.signal });
  }

  isUncovered() {
    const rect = this.getBoundingClientRect();
    const cover = this.cover.getBoundingClientRect();
    const container = getScrollContainer();
    const top = container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
    const bottom = top + (container === document.scrollingElement ? innerHeight : container.clientHeight);
    // Sticky artwork can intersect the viewport while still covered by the links.
    // Start only once at least 65% of the signature area is actually uncovered.
    const visible = Math.min(rect.bottom, bottom) - Math.max(rect.top, top, cover.bottom);
    return rect.height > 0 && rect.width > 0 && visible >= rect.height * .65;
  }

  schedule() {
    if (!this.near || this.played || this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      if (this.isUncovered()) this.start();
    });
  }

  start() {
    if (this.played || this.waiting) return;
    const play = () => {
      clearTimeout(this.timeout);
      this.waiting = false;
      if (this.controller.signal.aborted) return;
      if (!this.isUncovered()) return;
      this.played = true;
      this.dataset.phase = 'writing';
      this.observer?.disconnect();
      this.scrollController?.abort();
      try {
        for (const path of this.querySelectorAll('[data-write]')) {
          const length = path.getTotalLength();
          path.style.strokeDasharray = `${length} ${length}`;
          path.style.strokeDashoffset = `${length}`;
          path.style.visibility = 'visible';
          this.animations.push(path.animate([{ strokeDashoffset: `${length}` }, { strokeDashoffset: '0' }], {
            duration: Number(path.dataset.duration), delay: Number(path.dataset.delay), easing: 'linear', fill: 'both',
          }));
        }
        for (const patch of this.querySelectorAll('[data-complete]')) {
          this.animations.push(patch.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: 110, delay: Number(patch.dataset.delay), easing: 'ease-out', fill: 'both',
          }));
        }
        this.timeout = setTimeout(() => this.finish(), 4200);
        Promise.allSettled(this.animations.map((animation) => animation.finished)).then(() => this.finish());
      } catch (_) { this.finish(); }
    };
    this.source.loading = 'eager';
    if (this.source.complete && this.source.naturalWidth > 0) { play(); return; }
    this.waiting = true;
    this.source.addEventListener('load', play, { once: true, signal: this.controller.signal });
    this.source.addEventListener('error', () => this.finish(), { once: true, signal: this.controller.signal });
    this.timeout = setTimeout(() => this.finish(), 2500);
  }

  finish() {
    this.played = true;
    clearTimeout(this.timeout);
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    this.scrollController?.abort();
    this.controller?.abort();
    this.animations?.forEach((animation) => animation.cancel());
    this.classList.remove('is-armed');
    this.dataset.phase = 'complete';
  }

  disconnectedCallback() { this.finish(); }
}

if (!customElements.get('olsun-footer-signature')) customElements.define('olsun-footer-signature', OlsunFooterSignature);
